import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Banner from '@/components-latest/primitives/Banner'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Spinner from '@/components-latest/primitives/Spinner'
import Switch from '@/components-latest/primitives/Switch/Switch'
import Text from '@/components-latest/primitives/Text'
import AutoAcceptConfirmationModal from '@/components-latest/ui/AccountPage/AutoAcceptConfirmationModal/AutoAcceptConfirmationModal'
import AutoAcceptInfoModal from '@/components-latest/ui/AccountPage/AutoAcceptInfoModal/AutoAcceptInfoModal'
import { isErrorType } from '@/config/http/http.helpers'
import { STORE_AVAILABILITY } from '@/constants/common'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  useGetStoreAutoAcceptStatusQuery,
  useGetStoreBusyStatusQuery,
  useUpdateStoreAutoAcceptStatusMutation
} from '@/ducks/store/api/store.api'
import { selectStoreAvailability } from '@/ducks/store/selectors/store.selectors'

import Styles from './AutoAcceptSetting.module.scss'

const AutoAcceptSetting = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const storeAvailability = useSelector(selectStoreAvailability)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isBusyRestrictionOpen, setIsBusyRestrictionOpen] = useState(false)
  const [pendingEnabled, setPendingEnabled] = useState(false)

  const {
    data,
    isLoading: isStatusLoading,
    isError,
    error
  } = useGetStoreAutoAcceptStatusQuery()
  const { data: busyStatusData } = useGetStoreBusyStatusQuery()
  const [updateAutoAcceptStatus, { isLoading: isUpdating }] =
    useUpdateStoreAutoAcceptStatusMutation()

  const isEnabled = data?.store_auto_accept_orders_store_enabled === true
  const isLoading = isStatusLoading || isUpdating
  const isOffline = storeAvailability === STORE_AVAILABILITY.OFFLINE

  const unavailableMessage =
    isErrorType(error) && error.message
      ? error.message
      : t('account.order-preferences.unavailable')

  const openModal = (nextEnabled: boolean) => {
    setPendingEnabled(nextEnabled)
    setIsModalOpen(true)
  }

  const handleToggle = (nextEnabled: boolean) => {
    if (isLoading || isOffline) return

    if (nextEnabled && busyStatusData?.isBusy) {
      setIsBusyRestrictionOpen(true)
      return
    }

    openModal(nextEnabled)
  }

  const handleConfirm = async () => {
    try {
      await updateAutoAcceptStatus({
        store_auto_accept_orders_store_enabled: pendingEnabled
      }).unwrap()
      setIsModalOpen(false)
      dispatch(
        addSystemNotification({
          message: pendingEnabled
            ? t('account.order-preferences.auto-accept.enable-success')
            : t('account.order-preferences.auto-accept.disable-success'),
          intent: 'success',
          duration: 3000
        })
      )
    } catch {
      dispatch(
        addSystemNotification({
          message: t('account.order-preferences.auto-accept.error'),
          intent: 'error',
          duration: 3000
        })
      )
    }
  }

  if (isStatusLoading) {
    return (
      <div className={Styles.spinner}>
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return <Banner type="boxed" state="normal" description={unavailableMessage} />
  }

  return (
    <>
      <ContentBlock>
        <div className={Styles.row}>
          <div className={Styles.content}>
            <Text variant="nextSubtitle" weight="semiBold" className={Styles.title}>
              {t('account.order-preferences.auto-accept.title')}
            </Text>
            <Text variant="body" className={Styles.description}>
              {t('account.order-preferences.auto-accept.description')}
            </Text>
          </div>
          <div className={Styles.switchWrapper}>
            <Switch
              checked={isEnabled}
              onCheckedChange={handleToggle}
              isLoading={isLoading}
              disabled={isOffline}
              data-testid="auto-accept-switch"
            />
          </div>
        </div>
      </ContentBlock>

      <AutoAcceptInfoModal
        open={isBusyRestrictionOpen}
        onOpenChange={setIsBusyRestrictionOpen}
        title={t('account.order-preferences.auto-accept.busy-restriction.title')}
        description={t(
          'account.order-preferences.auto-accept.busy-restriction.description'
        )}
        confirmLabel={t(
          'account.order-preferences.auto-accept.busy-restriction.confirm'
        )}
      />

      <AutoAcceptConfirmationModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onConfirm={handleConfirm}
        isEnabling={pendingEnabled}
        isLoading={isUpdating}
      />
    </>
  )
}

export default AutoAcceptSetting
